import { get, ref, remove, update } from "firebase/database";
import { db } from "@/firebase/firebase";
import {
  decodeEmailFromRealtimeDbKey,
  encodeEmailForRealtimeDbKey,
} from "@/lib/helpers";

export type PartnerSeatsRecord = {
  seats: number;
  updatedAt?: number;
};

export type EmailSeatsRecord = {
  email?: string;
  seats: number;
  updatedAt?: number;
};

class AdminServiceClass {
  async getPartnerSeats() {
    const seatsRef = ref(db, "seats/partners");
    const snapshot = await get(seatsRef);
    return (snapshot.val() || {}) as Record<string, PartnerSeatsRecord>;
  }

  async getEmailSeats() {
    const seatsRef = ref(db, "seats/emails");
    const snapshot = await get(seatsRef);
    const rawData = (snapshot.val() || {}) as Record<string, EmailSeatsRecord>;

    return Object.fromEntries(
      Object.entries(rawData).map(([emailKey, data]) => [
        decodeEmailFromRealtimeDbKey(emailKey),
        data,
      ]),
    ) as Record<string, EmailSeatsRecord>;
  }

  async updatePartnerSeats(params: { partnerSlug: string; seats: number }) {
    const partnerSlug = params.partnerSlug.trim();
    const seats = Number(params.seats);

    if (!partnerSlug) {
      throw new Error("Partner slug is required");
    }

    if (!Number.isFinite(seats) || seats < 0) {
      throw new Error("Seats must be a non-negative number");
    }

    const partnerSeatsRef = ref(db, `seats/partners/${partnerSlug}`);
    await update(partnerSeatsRef, {
      updatedAt: Date.now(),
      seats,
    });
  }

  async deletePartnerSeats(params: { partnerSlug: string }) {
    const partnerSlug = params.partnerSlug.trim();

    if (!partnerSlug) {
      throw new Error("Partner slug is required");
    }

    const partnerSeatsRef = ref(db, `seats/partners/${partnerSlug}`);
    await remove(partnerSeatsRef);
  }

  async updateEmailSeats(params: {
    email: string;
    seats: number;
    users?: Record<string, { email?: string; emailEncoded?: string }>;
  }) {
    const email = params.email.toLowerCase().trim();
    const seats = Number(params.seats);

    if (!email) {
      throw new Error("Email is required");
    }

    if (!Number.isFinite(seats) || seats < 0) {
      throw new Error("Seats must be a non-negative number");
    }

    const emailKey = encodeEmailForRealtimeDbKey(email);
    const emailSeatsRef = ref(db, `seats/emails/${emailKey}`);
    await update(emailSeatsRef, {
      email,
      updatedAt: Date.now(),
      seats,
    });
    const users = params.users || {};

    const matchingUid = Object.entries(users).find(
      ([, user]) => user?.email?.toLowerCase().trim() === email,
    )?.[0];

    console.log("matchingUid", { matchingUid, emailKey, users });

    if (matchingUid) {
      await update(ref(db, "emailToUid"), { [emailKey]: matchingUid });
    }

    const userEmailEncodedUpdates = Object.entries(users).reduce(
      (acc, [uid, user]) => {
        const userEmail = user?.email?.toLowerCase().trim();
        if (!userEmail || userEmail !== email) return acc;

        if (user?.emailEncoded !== emailKey) {
          acc[`${uid}/emailEncoded`] = emailKey;
        }

        return acc;
      },
      {} as Record<string, string>,
    );

    if (Object.keys(userEmailEncodedUpdates).length) {
      await update(ref(db, "users"), userEmailEncodedUpdates);
    }
  }

  async deleteEmailSeats(params: { email: string }) {
    const email = params.email.toLowerCase().trim();
    if (!email) {
      throw new Error("Email is required");
    }

    const emailKey = encodeEmailForRealtimeDbKey(email);
    const emailSeatsRef = ref(db, `seats/emails/${emailKey}`);
    await remove(emailSeatsRef);
  }

}

export const AdminService = new AdminServiceClass();
